/** Shared helpers for downloading generated media (images/videos) to disk. */
import type { Job } from '@volt/shared';
import { getWebApp } from '../telegram/webapp.js';

/**
 * What to show inline for result `index`: the small server preview when there is one,
 * the original otherwise. Full-screen views and downloads always use the original.
 */
export function jobPreviewUrl(job: Job, index: number, original: string): string {
  return job.previewUrls?.[index] ?? original;
}

/** Path part of a URL, so query strings (signatures, tokens with dots) never look like extensions. */
function urlPath(url: string): string {
  try {
    return new URL(url, 'https://volt.invalid').pathname;
  } catch {
    return url.split('?')[0] ?? url;
  }
}

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v', 'ogg', 'ogv'];

function isCrossOrigin(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Trigger a download of a URL (http/https or data:) with a suggested filename.
 *
 * Inside Telegram the WebView cannot save files itself: Bot API 8.0+ offers
 * `downloadFile`, older clients can only hand a cross-origin link to the system
 * browser via `openLink`. A plain `<a download>` works for same-origin and data URLs.
 */
export function downloadUrl(url: string, filename: string): void {
  const app = getWebApp();
  const remote = /^https?:/i.test(url);
  if (app && remote && typeof app.downloadFile === 'function') {
    try {
      app.downloadFile({ url, file_name: filename });
      return;
    } catch {
      /* fall through to the other strategies */
    }
  }
  const crossOrigin = remote && isCrossOrigin(url);
  if (app && crossOrigin) {
    app.openLink(url);
    return;
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener noreferrer';
  // A cross-origin `download` is ignored by browsers; at least open it in a new tab.
  if (crossOrigin) a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Derive a file extension from a URL or data URL for the given media kind. */
export function getExtensionFromUrl(url: string, kind: 'image' | 'video' = 'image'): string {
  if (kind === 'image') {
    if (url.startsWith('data:image/png')) return 'png';
    if (url.startsWith('data:image/webp')) return 'webp';
    if (url.startsWith('data:image/gif')) return 'gif';
  } else {
    if (url.startsWith('data:video/mp4')) return 'mp4';
    if (url.startsWith('data:video/webm')) return 'webm';
  }
  const ext = urlPath(url).split('.').pop()?.toLowerCase();
  const allowed = kind === 'image' ? IMAGE_EXTENSIONS : VIDEO_EXTENSIONS;
  if (ext && allowed.includes(ext)) return ext;
  return kind === 'image' ? 'jpg' : 'mp4';
}

/** Heuristic: does this result URL point at a video file (vs. an image)? */
export function isVideoUrl(url: string): boolean {
  if (url.startsWith('data:video/')) return true;
  if (url.startsWith('data:image/')) return false;
  const ext = urlPath(url).split('.').pop()?.toLowerCase();
  return !!ext && VIDEO_EXTENSIONS.includes(ext);
}
